import { User, Permission, ROLE_PERMISSIONS, UserRole, ModuleId, MODULE_IDS, isPlatformRole, isOwnerRole } from '../domain/entities';
import { tabPermissionKey, PLATFORM_MODULES } from './appRegistry';

function hasExplicitScopeAssignments(user: Pick<User, 'assignedBrands' | 'assignedBranches'> | null): boolean {
  return ((user?.assignedBrands?.length ?? 0) > 0) || ((user?.assignedBranches?.length ?? 0) > 0);
}

export function hasPermission(user: User | null, permission: Permission): boolean {
  if (!user) return false;
  if (isOwnerRole(user.role)) return true; // Platform Owner bypasses all permission checks
  return (ROLE_PERMISSIONS[user.role] ?? []).includes(permission);
}

export function isWithinScope(
  user: User | null,
  scope: { branchId?: string; brandId?: string },
): boolean {
  if (!user) return false;
  if (isPlatformRole(user.role)) return true;
  if (!hasExplicitScopeAssignments(user)) return true;

  if (scope.branchId && (user.assignedBranches ?? []).includes(scope.branchId)) return true;
  if (scope.brandId && (user.assignedBrands ?? []).includes(scope.brandId)) return true;
  return false;
}

export function filterByScope<T extends { branchId?: string; brandId?: string }>(
  user: User | null,
  dataList: T[]
): T[] {
  if (!user) return [];
  if (isPlatformRole(user.role)) return dataList;
  if (!hasExplicitScopeAssignments(user)) return dataList;

  return dataList.filter(item => {
    if (item.branchId && (user.assignedBranches ?? []).includes(item.branchId)) {
      return true;
    }

    if (item.brandId && (user.assignedBrands ?? []).includes(item.brandId)) {
      return true;
    }

    return false;
  });
}

export function isWithinBrandScope(user: User | null, brandId: string | undefined): boolean {
  if (!user) return false;
  if (isPlatformRole(user.role)) return true;
  if (!hasExplicitScopeAssignments(user)) return true;
  if (!brandId || !user.assignedBrands || user.assignedBrands.length === 0) return false;
  return user.assignedBrands.includes(brandId);
}

/**
 * Hierarchical permission chain for module access:
 *   SUPER_ADMIN / PLATFORM_OWNER → bypasses everything
 *   PLATFORM_SUPPORT / SALES    → bypasses everything (platform staff)
 *   ADMIN / DEPARTMENT_HEAD / MANAGER / EMPLOYEE → checked against user.assignedModules
 *
 * The 'system-tools' module is always considered available (core platform, not installable).
 */
export function canAccessModule(
  user: User | null,
  moduleId: ModuleId,
  requiredPermission?: Permission,
  orgEnabledApps?: string[],
): boolean {
  if (!user) return false;
  if (isPlatformRole(user.role)) return true; // Platform staff can access all modules
  if (user.accessRestricted) return false; // Flagged users are locked out
  const isEmployeeSupportDesk = user.role === UserRole.EMPLOYEE && moduleId === MODULE_IDS.SUPPORT_DESK;

  // ── Layer 1: Organization License Gate (Dual-Key) ──
  // The 'system-tools' module is core platform — always available, never gated by enabledApps.
  // All other modules must be explicitly listed in the org's enabledApps.
  const isSystemModule = moduleId === MODULE_IDS.SYSTEM;
  if (!isSystemModule) {
    const orgApps = orgEnabledApps ?? [];
    if (!orgApps.includes(moduleId)) return false;
  }

  // Must pass permission check first
  if (requiredPermission && !hasPermission(user, requiredPermission)) return false;

  // ── Layer 2: Module supportedRoles Gate ──
  // If the module declares supportedRoles, the user's role must be in the list.
  const moduleDef = PLATFORM_MODULES.find(m => m.id === moduleId);
  if (moduleDef?.supportedRoles && !moduleDef.supportedRoles.includes(user.role)) return false;
  if (user.role === UserRole.ADMIN) return true;

  // ── Layer 3: User-Level Gate ──
  // Legacy ADMIN users with no saved module array still retain full access.
  // An empty array also means "no explicit restrictions" (e.g. freshly signed-up ADMIN
  // with no rows in user_assigned_modules yet).
  if (isEmployeeSupportDesk) return true;
  if (!user.assignedModules || user.assignedModules.length === 0) return false;
  return user.assignedModules.includes(moduleId);
}

/**
 * Hierarchical tab access:
 *   SUPER_ADMIN / PLATFORM_*  → bypasses everything (unrestricted)
 *   ADMIN / DEPARTMENT_HEAD / MANAGER / EMPLOYEE → checked against user.tabAccess array
 *
 * For tabs with variants: user needs at least one variant key to see the tab.
 * Legacy base keys (without variant suffix) are accepted as backward compat.
 */
export function canAccessTab(
  user: User | null,
  moduleId: string,
  tabId: string,
): boolean {
  if (!user) return false;
  // Platform staff always has full access
  if (isPlatformRole(user.role)) return true;
  if (user.role === UserRole.EMPLOYEE && moduleId === MODULE_IDS.SUPPORT_DESK && tabId === 'ticket-center') {
    return true;
  }
  if (user.role === UserRole.ADMIN) return true;
  // No tabAccess array (or empty) means the user has not been restricted yet.
  if (!user.tabAccess || user.tabAccess.length === 0) return true;

  // Check for variant-aware tabs
  const mod = PLATFORM_MODULES.find(m => m.id === moduleId);
  const tabDef = mod?.tabs.find(t => t.id === tabId);

  if (tabDef?.variants && tabDef.variants.length > 0) {
    // User needs at least ONE variant key to access the tab
    const hasAnyVariant = tabDef.variants.some(v =>
      user.tabAccess!.includes(tabPermissionKey(moduleId, tabId, v))
    );
    // Backward compat: legacy base key (no variant suffix) still grants access
    const hasLegacyKey = user.tabAccess.includes(tabPermissionKey(moduleId, tabId));
    return hasAnyVariant || hasLegacyKey;
  }

  // Non-variant tab: simple base key check
  return user.tabAccess.includes(tabPermissionKey(moduleId, tabId));
}

/**
 * Check whether a user holds a specific view variant for a tab.
 *
 * Bypass hierarchy (mirrors canAccessTab):
 *   SUPER_ADMIN / PLATFORM_*  → true (unrestricted)
 *   ADMIN / DEPARTMENT_HEAD / MANAGER / EMPLOYEE → checked against tabAccess variant keys
 *
 * Backward compat: if user still has a legacy base key (e.g. `aggregator:dashboard`)
 * for a tab that now defines variants, the base key is treated as granting ALL variants.
 */
export function hasTabVariant(
  user: User | null,
  moduleId: string,
  tabId: string,
  variant: string,
): boolean {
  if (!user) return false;
  if (isPlatformRole(user.role)) return true;
  if (user.role === UserRole.ADMIN) return true;
  // No tabAccess → legacy user, treat as full access
  if (!user.tabAccess) return true;
  // Check for the specific variant key
  if (user.tabAccess.includes(tabPermissionKey(moduleId, tabId, variant))) return true;
  // Backward compat: legacy base key grants all variants
  if (user.tabAccess.includes(tabPermissionKey(moduleId, tabId))) return true;
  return false;
}

function hasStrictTabGrant(user: User | null, moduleId: string, tabId: string): boolean {
  if (!user) return false;
  if (isPlatformRole(user.role) || user.role === UserRole.ADMIN) return true;

  const baseKey = tabPermissionKey(moduleId, tabId);
  return !!user.tabAccess?.some((key) => key === baseKey || key.startsWith(`${baseKey}:`));
}

export function canViewMenuSettings(user: User | null): boolean {
  return hasStrictTabGrant(user, 'menu-manage', 'settings');
}

export function canViewRiderSettings(user: User | null): boolean {
  return hasStrictTabGrant(user, 'rider-manager', 'settings');
}
